// A member deletes their own sent message, and deleting is ALWAYS for
// everyone (owner decision, 24 Sep 2026): the message becomes the "This
// message was deleted" placeholder for every participant. There is no
// "delete for me". The server allows it at any age.
//
// Mirror of the app's message_delete.dart: change both together.
// Pure, no Nuxt imports, so the unit tests import it directly.

import {DELETED_BY_SENDER, isTombstone, toStoredMessage} from './useMessageTombstone.js';

export const DELETE_MESSAGE_COPY = Object.freeze({
    title: 'Delete this message?',
    body: 'It will be deleted for everyone in this chat.',
    cancel: 'Cancel',
    confirm: 'Delete',
});

// Offline is REFUSED, not queued: a queued delete would show a placeholder the
// others cannot see yet, and a reload before it went would quietly undo it.
export const OFFLINE_DELETE_TEXT = "You're offline. Connect to the internet to delete this message.";
export const DELETE_FAILED_TEXT = "Couldn't delete the message. Try again.";

/**
 * Whether to offer "Delete" on a message: my own, already sent, not already
 * deleted. A failed or pending message keeps its own Retry/Delete handling,
 * since the server has no copy of it to delete.
 */
export const canDeleteForEveryone = (message, userId) =>
    !!message && !!userId
    && message.senderUserId === userId
    && !isTombstone(message)
    && (message._syncState == null || message._syncState === 'synced');

/** The sender's placeholder, as the server will send it back. */
export const asDeletedBySender = (message, now = new Date()) => toStoredMessage({
    ...message,
    deletedAt: now.toISOString(),
    deletedBy: DELETED_BY_SENDER,
});

/** The server's own sentence when it gave one, otherwise try again. */
export const deleteFailureText = (error) =>
    error?.global?.[0] || error?.data?.global?.[0] || DELETE_FAILED_TEXT;

/**
 * Whether the API's reply confirms THIS message was deleted: its tombstone,
 * with the id and when it went. Anything else (nothing at all, another
 * message, a reply with no deletedAt) is not confirmation. Guids are compared
 * without case, since the API writes them either way.
 */
export const confirmsDeletion = (reply, messageId) =>
    !!reply && typeof reply === 'object'
    && reply.id != null && messageId != null
    && String(reply.id).toLowerCase() === String(messageId).toLowerCase()
    && !!reply.deletedAt;

/**
 * Deletes a sent message, and only shows the placeholder once the server has
 * CONFIRMED it (24 Sep 2026). It used to be optimistic, and useCrudApi
 * swallowed network failures and resolved as though they had worked, so a
 * delete that never reached the server left the placeholder on screen and in
 * IndexedDB while everyone else still had the message. Now nothing is written
 * until the reply is the tombstone; on any failure the message is simply left
 * as it was. Returns {ok} or {ok: false, error} with the sentence to show.
 * Storage and the API are passed in so this stays testable.
 *
 * The app's deleteForEveryone is still optimistic with a restore: Dio throws
 * on a failed request, so it cannot mistake one for success.
 */
export async function deleteForEveryone({message, online, readStored, writeStored, refresh, callApi, now = new Date()}) {
    if (online === false) return {ok: false, error: OFFLINE_DELETE_TEXT};

    try {
        const reply = await callApi(message.id);
        if (!confirmsDeletion(reply, message.id)) return {ok: false, error: DELETE_FAILED_TEXT};
    } catch (error) {
        return {ok: false, error: deleteFailureText(error)};
    }

    const original = (await readStored(message.id)) ?? message;
    await writeStored(asDeletedBySender(original, now));
    await refresh();
    return {ok: true};
}
